import type { Placement } from '@popperjs/core';
import FocusTrap from 'focus-trap-react';
import { useOnClickOutside } from 'hem-hooks';
import { ChevronDown, ChevronUp } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { usePopper } from 'react-popper';

import { panel } from './Dropdown.module.scss';

type Props = {
  toggleElement: React.ReactNode;
  placement?: Placement;
  showCaret?: boolean;
  width?: number;
  keepInDom?: boolean;
  children: React.ReactNode;
};

export const Dropdown: React.FC<Props> = ({
  toggleElement,
  placement = 'bottom-start',
  showCaret = true,
  children,
  width = 200,
  keepInDom = false,
  ...rest
}) => {
  const [isClient, setIsClient] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [referenceElement, setReferenceElement] = useState(null);
  const [popperElement, setPopperElement] = useState(null);
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement,
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, 8],
        },
      },
    ],
  });

  const panelRef = useRef<null | HTMLDivElement>(null);

  useOnClickOutside(panelRef, (event) => {
    if ((event.target as HTMLElement).closest('button') !== referenceElement) {
      setIsOpen(false);
    }
  });

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.onkeydown = (event: KeyboardEvent) => {
        if (event.key === 'Escape' || event.key === 'Esc') {
          setIsOpen(false);
        }
      };
    } else {
      document.onkeydown = null;
    }
  }, [isOpen]);

  const toggleIsOpen = () => {
    setIsOpen(!isOpen);
  };

  return (
    <>
      <button
        className="flex items-center gap-2 text-black-400 hover:text-black-600"
        type="button"
        ref={setReferenceElement}
        onClick={toggleIsOpen}
        aria-expanded={isOpen}
        {...rest}
      >
        {toggleElement}
        {showCaret &&
          (isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />)}
      </button>

      {keepInDom && isClient && (
        <div
          ref={setPopperElement}
          style={{ zIndex: 2, width, ...styles.popper }}
          {...attributes.popper}
        >
          <div
            ref={panelRef}
            className={panel}
            style={{
              display: isOpen ? 'block' : 'none',
            }}
          >
            {children}
          </div>
        </div>
      )}

      {!keepInDom &&
        isClient &&
        ReactDOM.createPortal(
          <div
            ref={setPopperElement}
            style={{ zIndex: 2, width, ...styles.popper }}
            {...attributes.popper}
          >
            {isOpen && (
              <FocusTrap focusTrapOptions={{ allowOutsideClick: true }}>
                <div ref={panelRef} className={panel}>
                  {children}
                </div>
              </FocusTrap>
            )}
          </div>,

          document.body,
        )}
    </>
  );
};
